import { isStepUpDeclinedError } from '~/utils/step-up';

/**
 * Composable for centralized error and success notifications
 * Provides consistent notification handling across the application
 */
export const useErrorNotification = () => {
  const { $i18n, $q } = useNuxtApp();

  const notify = (
    type: 'negative' | 'positive' | 'warning' | 'info',
    translationKey: string,
    options?: { caption?: string; timeout?: number; params?: Record<string, unknown> }
  ) => {
    const translatedMessage = $i18n.t(translationKey, options?.params ?? {});
    $q.notify({
      type,
      message: translatedMessage,
      caption: options?.caption,
      position: 'top',
      timeout: options?.timeout ?? (type === 'negative' ? 3500 : 2400),
      classes: `gabber-toast gabber-toast--${type}`,
      textColor: 'white',
      icon:
        type === 'negative'
          ? 'error_outline'
          : type === 'positive'
            ? 'check_circle_outline'
            : type === 'warning'
              ? 'warning_amber'
              : 'info'
    });
  };

  /**
   * Shows an error notification to the user
   * @param translationKey - The i18n translation key for the error message
   * @param error - Optional error object for logging in development
   */
  const notifyError = (translationKey: string, error?: Error | unknown) => {
    // cas-4b78: declining the "sign in again" prompt is not a failure.
    if (isStepUpDeclinedError(error)) return;
    notify('negative', translationKey, { timeout: 3200 });

    // Log error details in development mode
    if (process.env.NODE_ENV === 'development' && error) {
      console.error(`[${translationKey}]:`, error);
    }
  };

  /**
   * Shows a success notification to the user
   * @param translationKey - The i18n translation key for the success message
   */
  const notifySuccess = (translationKey: string, params?: Record<string, unknown>) => {
    notify('positive', translationKey, { timeout: 2200, params });
  };

  /**
   * Shows a warning notification to the user
   * @param translationKey - The i18n translation key for the warning message
   */
  const notifyWarning = (translationKey: string) => {
    notify('warning', translationKey, { timeout: 2600 });
  };

  /**
   * Shows an info notification to the user
   * @param translationKey - The i18n translation key for the info message
   */
  const notifyInfo = (translationKey: string) => {
    notify('info', translationKey, { timeout: 2200 });
  };

  return {
    notifyError,
    notifySuccess,
    notifyWarning,
    notifyInfo
  };
};
